import { useState } from "react";

type Statement = { text: string; answer: boolean; explain: string };

const STARTER: Statement[] = [
  { text: "Saving $1 a day adds up to $365 in a year.",                            answer: true,  explain: "Small amounts really do add up — $1/day × 365 days = $365." },
  { text: "A debit card lets you spend money you haven't earned yet.",              answer: false, explain: "That's a credit card. A debit card only spends money already in your account." },
  { text: "Needs include things like food, clothing, and shelter.",                 answer: true,  explain: "Needs are things required to survive. Wants are extras — games, snacks, etc." },
  { text: "If something is on sale, you should always buy it.",                     answer: false, explain: "A deal on something you don't need is still spending money you didn't plan to spend." },
  { text: "Interest is money the bank pays you for keeping money there.",           answer: true,  explain: "Banks pay you a small percentage (interest) just for having an account with them." },
  { text: "Wants are things you must have to survive.",                             answer: false, explain: "Wants are things you'd like but don't need. Needs are what you can't live without." },
  { text: "A budget is a plan for how you'll spend your money.",                    answer: true,  explain: "With a budget, you decide where your money goes before you spend it." },
  { text: "Earning money by doing chores is an example of income.",                 answer: true,  explain: "Any money coming IN is income — allowance, chores, gifts, part-time jobs." },
  { text: "You can only save money if you earn a lot.",                             answer: false, explain: "Anyone can save — even $5/week adds up. The habit matters more than the amount." },
  { text: "A goal helps you save for something you want in the future.",            answer: true,  explain: "Setting a goal gives your savings a purpose and helps you stay motivated." },
];

const BUILDER: Statement[] = [
  { text: "Credit cards charge interest if you don't pay the full balance each month.", answer: true,  explain: "Carrying a balance means paying 20–30% interest on top of what you already owe." },
  { text: "Your credit score goes up when you miss payments.",                       answer: false, explain: "Missed payments hurt your score significantly — payment history is the biggest factor." },
  { text: "Compound interest means you earn interest on your interest.",             answer: true,  explain: "Over time this 'snowballs' — which is why starting early is so powerful." },
  { text: "Putting all your money in one stock is the safest investment strategy.",  answer: false, explain: "Concentration = high risk. Diversifying across many stocks or funds reduces risk." },
  { text: "A Roth IRA lets your money grow tax-free.",                               answer: true,  explain: "You pay taxes now, but all growth and withdrawals in retirement are tax-free." },
  { text: "The stock market goes up every single year.",                             answer: false, explain: "It drops regularly — but historically recovers and trends upward over decades." },
  { text: "An emergency fund should cover 3–6 months of expenses.",                 answer: true,  explain: "This cushion means a job loss or surprise bill doesn't derail your finances." },
  { text: "Inflation means your money buys MORE over time.",                        answer: false, explain: "Inflation means prices rise, so the same dollar buys LESS. That's why investing matters." },
  { text: "Starting to invest at 15 is better than waiting until 25.",              answer: true,  explain: "10 extra years of compound growth can more than double your final balance." },
  { text: "A 401(k) is a type of retirement savings account.",                      answer: true,  explain: "Many employers match contributions — that's free money you should never leave behind." },
];

const MASTER: Statement[] = [
  { text: "Paying only the minimum on a credit card costs you far more in interest.", answer: true,  explain: "A $1,000 balance at 24% APR paying minimums can take 10+ years and cost $2,000+ in interest." },
  { text: "Your net worth equals your assets plus your debts.",                      answer: false, explain: "Net worth = assets MINUS debts. Debt reduces your net worth, not adds to it." },
  { text: "Index funds beat most actively managed funds over 20 years.",            answer: true,  explain: "Over 90% of active fund managers underperform their benchmark index over 20 years." },
  { text: "Renting is always the worse financial choice compared to buying.",       answer: false, explain: "Renting can be smarter depending on how long you stay, local prices, and your flexibility needs." },
  { text: "Diversification reduces risk in an investment portfolio.",               answer: true,  explain: "Spreading investments across assets means one bad pick doesn't sink your whole portfolio." },
  { text: "High credit utilization helps your credit score.",                       answer: false, explain: "Using more than 30% of your credit limit hurts your score. Keep utilization low." },
  { text: "Compound interest works against you when you're in debt.",               answer: true,  explain: "The same compounding that grows savings also grows debt — the longer you wait, the more you owe." },
  { text: "You should check your credit report once every five years.",             answer: false, explain: "Check it at least once a year. You're entitled to a free report annually from each bureau." },
  { text: "An HSA (Health Savings Account) has triple tax advantages.",             answer: true,  explain: "Contributions are pre-tax, growth is tax-free, and withdrawals for medical costs are tax-free." },
  { text: "Dollar-cost averaging means investing a fixed amount on a regular schedule.", answer: true, explain: "Buying consistently regardless of price means you automatically buy more when prices are low." },
];

const STREAK_FIRE = (s: number) => s >= 5 ? "🔥🔥🔥" : s >= 3 ? "🔥🔥" : s >= 2 ? "🔥" : "";

export default function TrueFalseGame({ onComplete, color, dark, tier = "builder" }: any) {
  const POOL = tier === "starter" ? STARTER : tier === "master" ? MASTER : BUILDER;

  const [items] = useState<Statement[]>(() =>
    [...POOL].sort(() => Math.random() - 0.5).slice(0, 8)
  );
  const [idx,     setIdx]     = useState(0);
  const [score,   setScore]   = useState(0);
  const [streak,  setStreak]  = useState(0);
  const [picked,  setPicked]  = useState<boolean | null>(null);
  const [done,    setDone]    = useState(false);

  const cur = items[idx];

  function pick(val: boolean) {
    if (picked !== null) return;
    setPicked(val);
    const ok = val === cur.answer;
    if (ok) { setScore(s => s + 1); setStreak(s => s + 1); }
    else    { setStreak(0); }
    setTimeout(() => {
      setPicked(null);
      if (idx + 1 >= items.length) { setDone(true); setTimeout(() => onComplete(score + (ok ? 1 : 0)), 1000); }
      else setIdx(i => i + 1);
    }, 1800);
  }

  const fire = STREAK_FIRE(streak);
  const isRevealed = picked !== null;
  const wasCorrect = isRevealed && picked === cur.answer;

  return (
    <div style={{ fontFamily: "Nunito" }}>
      {/* Top bar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ background: `linear-gradient(135deg,${color},${dark})`, borderRadius: 12, padding: "4px 12px" }}>
          <span style={{ fontSize: 15, fontWeight: 900, color: "#fff" }}>⭐ {score}/{items.length}</span>
        </div>
        {fire
          ? <span style={{ fontSize: 18, fontWeight: 900, color: "#f97316" }}>{fire} ×{streak}</span>
          : <span />}
        <span style={{ fontSize: 12, fontWeight: 800, color: "#9ca3af" }}>{idx + 1}/{items.length}</span>
      </div>

      {/* Progress bar */}
      <div style={{ display: "flex", gap: 5, marginBottom: 14 }}>
        {items.map((_, i) => (
          <div key={i} style={{
            flex: 1, height: 6, borderRadius: 99,
            background: i < idx ? color : i === idx ? dark : "#e5e7eb",
            transition: "background 0.3s",
          }} />
        ))}
      </div>

      {!done ? (
        <>
          {/* Statement card */}
          <div style={{
            background: `linear-gradient(135deg,${color}20,${color}08)`,
            border: `2.5px solid ${color}55`, borderRadius: 22, padding: "22px 16px",
            textAlign: "center", marginBottom: 18, boxShadow: `0 6px 24px ${color}28`,
            minHeight: 110, display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", gap: 10,
          }}>
            <div style={{ fontSize: 11, fontWeight: 900, color: dark, textTransform: "uppercase", letterSpacing: 2 }}>
              True or False?
            </div>
            <div style={{ fontSize: 17, fontWeight: 800, color: "#1f2937", lineHeight: 1.4 }}>
              "{cur.text}"
            </div>
          </div>

          {/* Explanation (shown after pick) */}
          {isRevealed && (
            <div style={{
              background: wasCorrect ? "#d1fae5" : "#fee2e2",
              border: `2px solid ${wasCorrect ? "#10b981" : "#ef4444"}`,
              borderRadius: 14, padding: "10px 14px", marginBottom: 14,
              fontSize: 13, fontWeight: 700, color: wasCorrect ? "#065f46" : "#991b1b",
              lineHeight: 1.4, textAlign: "center",
              animation: "pop 0.3s",
            }}>
              {wasCorrect ? "✅ " : "❌ "}{cur.explain}
            </div>
          )}

          {/* True / False buttons */}
          <div style={{ display: "flex", gap: 12 }}>
            {([true, false] as boolean[]).map(val => {
              const label = val ? "TRUE" : "FALSE";
              const isSelected = isRevealed && picked === val;
              const isCorrectChoice = isRevealed && cur.answer === val;

              let bg = val
                ? "linear-gradient(135deg,#d1fae5,#a7f3d0)"
                : "linear-gradient(135deg,#fee2e2,#fecaca)";
              let border = val ? "#10b981" : "#ef4444";
              let textColor = val ? "#065f46" : "#991b1b";
              let opacity = 1;

              if (isRevealed) {
                if (isCorrectChoice) {
                  bg = "linear-gradient(135deg,#d1fae5,#a7f3d0)";
                  border = "#10b981";
                  textColor = "#065f46";
                } else if (isSelected) {
                  bg = "linear-gradient(135deg,#fee2e2,#fecaca)";
                  border = "#ef4444";
                  textColor = "#991b1b";
                } else {
                  opacity = 0.35;
                }
              }

              return (
                <button
                  key={label}
                  onClick={() => pick(val)}
                  className="btn-bounce"
                  style={{
                    flex: 1, background: bg, border: `2.5px solid ${border}`,
                    borderRadius: 18, padding: "20px 12px",
                    fontSize: 22, fontWeight: 900, color: textColor,
                    cursor: isRevealed ? "default" : "pointer",
                    transition: "all 0.2s", opacity,
                    boxShadow: `0 4px 16px ${border}44`,
                    fontFamily: "Fredoka One",
                  }}
                >
                  {label}
                  {isRevealed && isCorrectChoice && " ✅"}
                  {isRevealed && isSelected && !isCorrectChoice && " ❌"}
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <div style={{ textAlign: "center", padding: "20px 0", animation: "pop 0.4s" }}>
          <div style={{ fontSize: 56 }}>{score >= items.length ? "🏆" : score >= 6 ? "⭐" : "📚"}</div>
          <div style={{ fontSize: 22, fontWeight: 900, color, marginTop: 8 }}>
            {score >= items.length ? "Perfect score!" : score >= 6 ? "Sharp thinking!" : "Good practice!"}
          </div>
          <div style={{ fontSize: 32, fontWeight: 900, color: dark, marginTop: 4 }}>{score}/{items.length}</div>
          <div style={{ fontSize: 13, color: "#6b7280", fontWeight: 700, marginTop: 6 }}>
            The more you know, the better your money decisions.
          </div>
        </div>
      )}
    </div>
  );
}
